/**
 * Phase 6.5: What-If Scenario Calculator
 *
 * Calculates impact metrics for scenario exploration.
 */

import {
  MECETable,
  MECEUserState,
  WhatIfScenario,
  ScenarioTacticSelection,
  ScenarioImpactMetrics,
  ScenarioComparison,
  TacticInclusionState
} from '@models/index'

/**
 * Create a baseline scenario with all tactics included
 */
export function createBaselineScenario(
  meceTable: MECETable,
  id: string = 'baseline'
): WhatIfScenario {
  const selections: ScenarioTacticSelection[] = []

  meceTable.pillars.forEach((pillar, pillarIdx) => {
    pillar.tactics.forEach((_, tacticIdx) => {
      selections.push({
        pillar_index: pillarIdx,
        tactic_index: tacticIdx,
        inclusion: 'included'
      })
    })
  })

  return {
    id,
    name: 'Current Plan (Baseline)',
    description: 'All tactics included as originally proposed',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    tactic_selections: selections,
    is_baseline: true
  }
}

/**
 * Create a new empty scenario (all tactics as "maybe")
 */
export function createEmptyScenario(
  meceTable: MECETable,
  name: string,
  description?: string
): WhatIfScenario {
  const selections: ScenarioTacticSelection[] = []

  meceTable.pillars.forEach((pillar, pillarIdx) => {
    pillar.tactics.forEach((_, tacticIdx) => {
      selections.push({
        pillar_index: pillarIdx,
        tactic_index: tacticIdx,
        inclusion: 'maybe'
      })
    })
  })

  const now = new Date().toISOString()
  return {
    id: `scenario_${Date.now()}`,
    name,
    description,
    created_at: now,
    updated_at: now,
    tactic_selections: selections,
    is_baseline: false
  }
}

/**
 * Clone an existing scenario
 */
export function cloneScenario(
  scenario: WhatIfScenario,
  newName: string
): WhatIfScenario {
  const now = new Date().toISOString()
  return {
    ...scenario,
    id: `scenario_${Date.now()}`,
    name: newName,
    description: `Cloned from "${scenario.name}"`,
    created_at: now,
    updated_at: now,
    tactic_selections: scenario.tactic_selections.map(s => ({ ...s })),
    is_baseline: false
  }
}

/**
 * Update a tactic's inclusion state in a scenario
 */
export function updateTacticInclusion(
  scenario: WhatIfScenario,
  pillarIndex: number,
  tacticIndex: number,
  inclusion: TacticInclusionState,
  impactNotes?: string
): WhatIfScenario {
  const updatedSelections = scenario.tactic_selections.map(selection => {
    if (selection.pillar_index === pillarIndex && selection.tactic_index === tacticIndex) {
      return {
        ...selection,
        inclusion,
        impact_notes: impactNotes
      }
    }
    return selection
  })

  return {
    ...scenario,
    updated_at: new Date().toISOString(),
    tactic_selections: updatedSelections
  }
}

/**
 * Get the inclusion state for a specific tactic in a scenario
 */
export function getTacticInclusion(
  scenario: WhatIfScenario,
  pillarIndex: number,
  tacticIndex: number
): TacticInclusionState {
  const selection = scenario.tactic_selections.find(
    s => s.pillar_index === pillarIndex && s.tactic_index === tacticIndex
  )
  return selection?.inclusion || 'maybe'
}

/**
 * Calculate impact metrics for a scenario
 */
export function calculateScenarioMetrics(
  scenario: WhatIfScenario,
  meceTable: MECETable,
  userState?: MECEUserState | null
): ScenarioImpactMetrics {
  let totalTactics = 0
  let includedTactics = 0
  let excludedTactics = 0
  let maybeTactics = 0

  const coverageByPillar: Record<string, { total: number; included: number; percentage: number }> = {}
  const effortCounts = { low: 0, medium: 0, high: 0 }

  // BSC perspective tracking (simplified - based on pillar keywords)
  const bscCoverage: Record<string, number> = {
    'Financial': 0,
    'Customer': 0,
    'Internal Process': 0,
    'Learning & Growth': 0
  }
  const bscTotals: Record<string, number> = {
    'Financial': 0,
    'Customer': 0,
    'Internal Process': 0,
    'Learning & Growth': 0
  }

  // High-priority tactics tracking for strategic alignment
  let highPriorityIncluded = 0
  let highPriorityTotal = 0

  meceTable.pillars.forEach((pillar, pillarIdx) => {
    const pillarName = pillar.name
    coverageByPillar[pillarName] = { total: 0, included: 0, percentage: 0 }

    // Detect BSC perspective from pillar name
    const bscPerspective = detectBSCPerspective(pillarName)

    pillar.tactics.forEach((tactic, tacticIdx) => {
      totalTactics++
      coverageByPillar[pillarName].total++
      bscTotals[bscPerspective]++

      // Check if high priority (top 3 relevance or explicit priority)
      const isHighPriority = (tactic.priority !== undefined && tactic.priority <= 3) ||
                            (tactic.relevance_score !== undefined && tactic.relevance_score >= 0.7)
      if (isHighPriority) {
        highPriorityTotal++
      }

      // Get inclusion state
      const inclusion = getTacticInclusion(scenario, pillarIdx, tacticIdx)

      if (inclusion === 'included') {
        includedTactics++
        coverageByPillar[pillarName].included++
        bscCoverage[bscPerspective]++
        if (isHighPriority) highPriorityIncluded++
      } else if (inclusion === 'excluded') {
        excludedTactics++
      } else {
        maybeTactics++
      }

      // Get effort estimate from user state
      const userTacticState = userState?.pillars
        ?.find(p => p.pillar_index === pillarIdx)
        ?.tactics?.find(t => t.tactic_index === tacticIdx)

      if (inclusion === 'included' && userTacticState?.estimated_effort) {
        effortCounts[userTacticState.estimated_effort]++
      }
    })

    // Calculate pillar coverage percentage
    if (coverageByPillar[pillarName].total > 0) {
      coverageByPillar[pillarName].percentage =
        Math.round((coverageByPillar[pillarName].included / coverageByPillar[pillarName].total) * 100)
    }
  })

  // Calculate BSC coverage percentages
  Object.keys(bscCoverage).forEach(perspective => {
    if (bscTotals[perspective] > 0) {
      bscCoverage[perspective] = Math.round((bscCoverage[perspective] / bscTotals[perspective]) * 100)
    }
  })

  // Calculate risk score (higher exclusions = higher risk)
  const exclusionRate = totalTactics > 0 ? excludedTactics / totalTactics : 0
  const maybeRate = totalTactics > 0 ? maybeTactics / totalTactics : 0
  const riskScore = Math.round((exclusionRate * 70) + (maybeRate * 30))

  // Calculate strategic alignment score
  const strategicAlignmentScore = highPriorityTotal > 0
    ? Math.round((highPriorityIncluded / highPriorityTotal) * 100)
    : 100

  return {
    total_tactics: totalTactics,
    included_tactics: includedTactics,
    excluded_tactics: excludedTactics,
    maybe_tactics: maybeTactics,
    coverage_by_pillar: coverageByPillar,
    bsc_coverage: bscCoverage,
    estimated_effort: effortCounts,
    risk_score: riskScore,
    strategic_alignment_score: strategicAlignmentScore
  }
}

/**
 * Compare two scenarios
 */
export function compareScenarios(
  scenarioA: WhatIfScenario,
  scenarioB: WhatIfScenario,
  meceTable: MECETable,
  userState?: MECEUserState | null
): ScenarioComparison {
  const metricsA = calculateScenarioMetrics(scenarioA, meceTable, userState)
  const metricsB = calculateScenarioMetrics(scenarioB, meceTable, userState)

  const newlyIncluded: ScenarioTacticSelection[] = []
  const newlyExcluded: ScenarioTacticSelection[] = []

  // Find differences between scenarios
  scenarioA.tactic_selections.forEach(selectionA => {
    const selectionB = scenarioB.tactic_selections.find(
      s => s.pillar_index === selectionA.pillar_index && s.tactic_index === selectionA.tactic_index
    )

    if (selectionB) {
      if (selectionA.inclusion !== 'included' && selectionB.inclusion === 'included') {
        newlyIncluded.push(selectionB)
      } else if (selectionA.inclusion === 'included' && selectionB.inclusion === 'excluded') {
        newlyExcluded.push(selectionB)
      }
    }
  })

  // Calculate coverage delta
  const coverageA = metricsA.total_tactics > 0
    ? (metricsA.included_tactics / metricsA.total_tactics) * 100
    : 0
  const coverageB = metricsB.total_tactics > 0
    ? (metricsB.included_tactics / metricsB.total_tactics) * 100
    : 0

  return {
    scenario_a: scenarioA,
    scenario_b: scenarioB,
    metrics_a: metricsA,
    metrics_b: metricsB,
    differences: {
      newly_included: newlyIncluded,
      newly_excluded: newlyExcluded,
      coverage_delta: Math.round(coverageB - coverageA),
      effort_delta: (metricsB.estimated_effort.high - metricsA.estimated_effort.high) +
                   (metricsB.estimated_effort.medium - metricsA.estimated_effort.medium) * 0.5,
      risk_delta: metricsB.risk_score - metricsA.risk_score
    }
  }
}

/**
 * Detect BSC perspective from pillar name (simplified heuristic)
 */
function detectBSCPerspective(pillarName: string): string {
  const name = pillarName.toLowerCase()

  if (name.includes('financial') || name.includes('revenue') || name.includes('profit') ||
      name.includes('cost') || name.includes('margin') || name.includes('growth')) {
    return 'Financial'
  }
  if (name.includes('customer') || name.includes('client') || name.includes('satisfaction') ||
      name.includes('market') || name.includes('retention') || name.includes('acquisition')) {
    return 'Customer'
  }
  if (name.includes('learning') || name.includes('growth') || name.includes('talent') ||
      name.includes('training') || name.includes('capability') || name.includes('skill') ||
      name.includes('technology') || name.includes('innovation')) {
    return 'Learning & Growth'
  }
  return 'Internal Process'
}

/**
 * Generate scenario summary text
 */
export function generateScenarioSummary(
  scenario: WhatIfScenario,
  metrics: ScenarioImpactMetrics
): string {
  const coveragePercent = metrics.total_tactics > 0
    ? Math.round((metrics.included_tactics / metrics.total_tactics) * 100)
    : 0

  let summary = `**${scenario.name}**\n\n`
  summary += `- Coverage: ${coveragePercent}% (${metrics.included_tactics}/${metrics.total_tactics} tactics)\n`
  summary += `- Risk Score: ${metrics.risk_score}/100\n`
  summary += `- Strategic Alignment: ${metrics.strategic_alignment_score}%\n`

  if (metrics.maybe_tactics > 0) {
    summary += `- Pending Decisions: ${metrics.maybe_tactics} tactics\n`
  }

  return summary
}

// ============================================================================
// Phase 5: Propagation + Monte Carlo extensions
// ============================================================================

/** BSC propagation chain link from backend scenario analysis */
export interface PropagationChainLink {
  from_perspective: string
  to_perspective: string
  weight: number  // 0-1 propagation weight
  mechanisms: string[]
}

/** Affected metric from propagation analysis */
export interface AffectedMetric {
  perspective: string
  expected_delta_pct: number
  confidence: number  // 0-1
}

/** Full propagation result from backend */
export interface PropagationResult {
  chain: PropagationChainLink[]
  affected_metrics: Record<string, AffectedMetric>
  net_financial_impact_pct: number
}

/** Simplified chain view for ScenarioComparisonView compatibility */
export interface PropagationChain {
  source_node: string
  source_perspective: string
  impacts: PropagationImpact[]
}

export interface PropagationImpact {
  target_node: string
  target_perspective: string
  weight: number
  direction: 'positive' | 'negative'
  explanation: string
}

/** Convert backend PropagationResult to display-friendly PropagationChain[] */
export function propagationResultToChains(result: PropagationResult): PropagationChain[] {
  return result.chain.map(link => ({
    source_node: link.from_perspective,
    source_perspective: link.from_perspective,
    impacts: [{
      target_node: link.to_perspective,
      target_perspective: link.to_perspective,
      weight: link.weight,
      direction: link.weight >= 0 ? 'positive' as const : 'negative' as const,
      explanation: link.mechanisms.join(', '),
    }],
  }))
}

/** Monte Carlo simulation results from backend */
export interface MonteCarloResult {
  metric_key: string
  metric_name: string
  bands: {
    p10: number
    p25: number
    p50: number
    p75: number
    p90: number
  }
  current_value?: number
  unit?: string
}

/** Extended scenario with backend analysis data */
export interface AnalyzedScenario {
  id: number
  company_id: number
  name: string
  description?: string
  scenario_type: string
  changes: Record<string, any>
  propagation?: PropagationResult
  simulation?: {
    results: MonteCarloResult[]
    iterations: number
    timestamp: string
  }
  status: string
  created_at: string
  updated_at: string
}

/** Format confidence band for display */
export function formatConfidenceBand(result: MonteCarloResult): string {
  const { bands, unit } = result
  const u = unit || ''
  return `${bands.p10}${u} — ${bands.p25}${u} — ${bands.p50}${u} — ${bands.p75}${u} — ${bands.p90}${u}`
}

/** Get risk level from Monte Carlo bands */
export function getSimulationRiskLevel(result: MonteCarloResult): 'low' | 'medium' | 'high' {
  const spread = result.bands.p90 - result.bands.p10
  const median = result.bands.p50
  if (median === 0) return 'high'
  const cv = spread / Math.abs(median)
  if (cv < 0.3) return 'low'
  if (cv < 0.7) return 'medium'
  return 'high'
}
